// 📁 contexts/PacContext.js
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { supabase } from '../services/supabaseClient';

const PacContext = createContext();

export const usePac = () => {
  const context = useContext(PacContext);
  if (!context) {
    throw new Error('usePac must be used within a PacProvider');
  }
  return context;
};

export const PacProvider = ({ children }) => {
  // Estados para PAC Vigente (mantenidos)
  const [pacVigenteData, setPacVigenteData] = useState([]);
  const [pacVigenteLoading, setPacVigenteLoading] = useState(false);
  const [pacVigenteError, setPacVigenteError] = useState(null);
  const [pacVigenteLastUpdate, setPacVigenteLastUpdate] = useState(null);
  const [pacVigenteInitialized, setPacVigenteInitialized] = useState(false);

  // ✅ Estados para los indicadores PAC Vigente precalculados
  const [indicadoresPacVigente, setIndicadoresPacVigente] = useState({
    pac_vigente_procesos_total: 0,
    pac_vigente_entidades: 0
  });
  const [indicadoresPacVigenteLoading, setIndicadoresPacVigenteLoading] = useState(false);
  const [indicadoresPacVigenteError, setIndicadoresPacVigenteError] = useState(null);
  const [indicadoresPacVigenteLastUpdate, setIndicadoresPacVigenteLastUpdate] = useState(null);
  const [indicadoresPacVigenteInitialized, setIndicadoresPacVigenteInitialized] = useState(false);

  // ✅ Estados para los indicadores PAC Programado precalculados
  const [indicadoresPacProgramado, setIndicadoresPacProgramado] = useState({
    procesos_pac_programado: 0,
    entidades_pac_programado: 0
  });
  const [indicadoresPacProgramadoLoading, setIndicadoresPacProgramadoLoading] = useState(false);
  const [indicadoresPacProgramadoError, setIndicadoresPacProgramadoError] = useState(null);
  const [indicadoresPacProgramadoLastUpdate, setIndicadoresPacProgramadoLastUpdate] = useState(null);
  const [indicadoresPacProgramadoInitialized, setIndicadoresPacProgramadoInitialized] = useState(false);

  // ✅ CONSTANTE PARA EL TIEMPO DE CACHÉ (5 minutos)
  const CACHE_DURATION = 60* 60 * 1000;

  // ✅ FUNCIÓN para verificar si el caché es válido
  const isCacheValid = useCallback((cacheKey) => {
    try {
      const cachedTimestamp = localStorage.getItem(`${cacheKey}_timestamp`);
      if (!cachedTimestamp) {
        return false;
      }
      
      const cacheTime = new Date(cachedTimestamp);
      const now = new Date();
      const timeDiff = now.getTime() - cacheTime.getTime();
      
      return timeDiff < CACHE_DURATION;
    } catch (error) {
      console.error(`❌ Error verificando caché ${cacheKey}:`, error);
      return false;
    }
  }, [CACHE_DURATION]);

  // ✅ FUNCIÓN para cargar datos desde el caché
  const loadFromCache = useCallback((cacheKey) => {
    try {
      console.log(`🔍 Cargando ${cacheKey} desde caché...`);
      
      const cachedData = localStorage.getItem(`${cacheKey}_data`);
      const cachedTimestamp = localStorage.getItem(`${cacheKey}_timestamp`);
      
      if (cachedData && cachedTimestamp) {
        const parsedData = JSON.parse(cachedData);
        const timestamp = new Date(cachedTimestamp);
        
        console.log(`📦 ✅ ${cacheKey} cargado desde caché:`, parsedData);
        return { data: parsedData, timestamp };
      }
      
      return null;
    } catch (error) {
      console.error(`❌ Error cargando ${cacheKey} desde caché:`, error);
      localStorage.removeItem(`${cacheKey}_data`);
      localStorage.removeItem(`${cacheKey}_timestamp`);
      return null;
    }
  }, []);

  // ✅ FUNCIÓN para guardar datos en el caché
  const saveToCache = useCallback((cacheKey, dataToCache) => {
    try {
      const timestamp = new Date().toISOString();
      localStorage.setItem(`${cacheKey}_data`, JSON.stringify(dataToCache));
      localStorage.setItem(`${cacheKey}_timestamp`, timestamp);
      console.log(`💾 ${cacheKey} guardado en caché:`, dataToCache);
    } catch (error) {
      console.error(`❌ Error guardando ${cacheKey} en caché:`, error);
    }
  }, []);

  // ✅ FUNCIÓN para cargar indicadores PAC Vigente desde Supabase
  const loadIndicadoresPacVigenteFromSupabase = useCallback(async () => {
    console.log('🌐 Cargando indicadores PAC Vigente desde Supabase...');
    
    try {
      const { data: indicadoresData, error } = await supabase
        .from('indicadores_pac_vigente')
        .select('pac_vigente_procesos_total, pac_vigente_entidades')
        .single();

      if (error) throw error;
      
      console.log('✅ Indicadores PAC Vigente obtenidos:', indicadoresData);
      return indicadoresData || { pac_vigente_procesos_total: 0, pac_vigente_entidades: 0 };
    } catch (error) {
      console.error('❌ Error al cargar indicadores PAC Vigente:', error);
      throw error;
    }
  }, []);

  // ✅ FUNCIÓN para cargar indicadores PAC Programado desde Supabase
  const loadIndicadoresPacProgramadoFromSupabase = useCallback(async () => {
    console.log('🌐 Cargando indicadores PAC Programado desde Supabase...');
    
    try {
      const { data: indicadoresData, error } = await supabase
        .from('indicadores_pac_programado')
        .select('procesos_pac_programado, entidades_pac_programado')
        .single();

      if (error) throw error;
      
      console.log('✅ Indicadores PAC Programado obtenidos:', indicadoresData);
      return indicadoresData || { procesos_pac_programado: 0, entidades_pac_programado: 0 };
    } catch (error) {
      console.error('❌ Error al cargar indicadores PAC Programado:', error);
      throw error;
    }
  }, []);

  // ✅ FUNCIÓN para cargar indicadores PAC Vigente con caché
  const loadIndicadoresPacVigente = useCallback(async (forceRefresh = false) => {
    console.log(`🔄 loadIndicadoresPacVigente llamado. forceRefresh: ${forceRefresh}`);
    
    const cacheKey = 'indicadores_pac_vigente_cache';
    
    // Si ya tenemos datos válidos y no es refresh forzado, no hacer nada
    if (!forceRefresh && indicadoresPacVigente.pac_vigente_procesos_total > 0 && isCacheValid(cacheKey)) {
      console.log('⚡ Ya tenemos indicadores PAC Vigente válidos en contexto');
      return;
    }
    
    // Si no es refresh forzado, verificar caché primero
    if (!forceRefresh && indicadoresPacVigente.pac_vigente_procesos_total === 0) {
      if (isCacheValid(cacheKey)) {
        console.log('⚡ Caché indicadores PAC Vigente válido - Carga INSTANTÁNEA');
        const cached = loadFromCache(cacheKey);
        if (cached) {
          setIndicadoresPacVigente(cached.data);
          setIndicadoresPacVigenteLastUpdate(cached.timestamp);
          setIndicadoresPacVigenteError(null);
          setIndicadoresPacVigenteInitialized(true);
          return;
        }
      } else {
        console.log('🕒 Caché indicadores PAC Vigente expirado, cargando como placeholder...');
        const cached = loadFromCache(cacheKey);
        if (cached) {
          setIndicadoresPacVigente(cached.data);
          setIndicadoresPacVigenteLastUpdate(cached.timestamp);
        }
      }
    }

    // Cargar desde servidor
    console.log('🌐 Cargando indicadores PAC Vigente desde servidor...');
    setIndicadoresPacVigenteLoading(true);
    setIndicadoresPacVigenteError(null);
    
    try {
      const freshData = await loadIndicadoresPacVigenteFromSupabase();
      
      setIndicadoresPacVigente(freshData);
      const updateTime = new Date();
      setIndicadoresPacVigenteLastUpdate(updateTime);
      setIndicadoresPacVigenteInitialized(true);
      
      saveToCache(cacheKey, freshData);
      
      console.log('✅ Indicadores PAC Vigente frescos cargados:', freshData);
    } catch (error) {
      console.error('❌ Error al cargar indicadores PAC Vigente:', error);
      setIndicadoresPacVigenteError('Error al cargar indicadores PAC Vigente. Por favor, intente nuevamente.');
      
      if (indicadoresPacVigente.pac_vigente_procesos_total === 0) {
        const cached = loadFromCache(cacheKey);
        if (cached) {
          setIndicadoresPacVigente(cached.data);
          setIndicadoresPacVigenteLastUpdate(cached.timestamp);
          setIndicadoresPacVigenteError('Mostrando indicadores PAC Vigente en caché. Algunos datos pueden no estar actualizados.');
          setIndicadoresPacVigenteInitialized(true);
        }
      }
    } finally {
      setIndicadoresPacVigenteLoading(false);
    }
  }, [isCacheValid, loadFromCache, loadIndicadoresPacVigenteFromSupabase, saveToCache, indicadoresPacVigente.pac_vigente_procesos_total]);

  // ✅ FUNCIÓN para cargar indicadores PAC Programado con caché
  const loadIndicadoresPacProgramado = useCallback(async (forceRefresh = false) => {
    console.log(`🔄 loadIndicadoresPacProgramado llamado. forceRefresh: ${forceRefresh}`);
    
    const cacheKey = 'indicadores_pac_programado_cache';
    
    // Si ya tenemos datos válidos y no es refresh forzado, no hacer nada
    if (!forceRefresh && indicadoresPacProgramado.procesos_pac_programado > 0 && isCacheValid(cacheKey)) {
      console.log('⚡ Ya tenemos indicadores PAC Programado válidos en contexto');
      return;
    }
    
    // Si no es refresh forzado, verificar caché primero
    if (!forceRefresh && indicadoresPacProgramado.procesos_pac_programado === 0) {
      if (isCacheValid(cacheKey)) {
        console.log('⚡ Caché indicadores PAC Programado válido - Carga INSTANTÁNEA');
        const cached = loadFromCache(cacheKey);
        if (cached) {
          setIndicadoresPacProgramado(cached.data);
          setIndicadoresPacProgramadoLastUpdate(cached.timestamp);
          setIndicadoresPacProgramadoError(null);
          setIndicadoresPacProgramadoInitialized(true);
          return;
        }
      } else {
        console.log('🕒 Caché indicadores PAC Programado expirado, cargando como placeholder...');
        const cached = loadFromCache(cacheKey);
        if (cached) {
          setIndicadoresPacProgramado(cached.data);
          setIndicadoresPacProgramadoLastUpdate(cached.timestamp);
        }
      }
    }

    // Cargar desde servidor
    console.log('🌐 Cargando indicadores PAC Programado desde servidor...');
    setIndicadoresPacProgramadoLoading(true);
    setIndicadoresPacProgramadoError(null);
    
    try {
      const freshData = await loadIndicadoresPacProgramadoFromSupabase();
      
      setIndicadoresPacProgramado(freshData);
      const updateTime = new Date();
      setIndicadoresPacProgramadoLastUpdate(updateTime);
      setIndicadoresPacProgramadoInitialized(true);
      
      saveToCache(cacheKey, freshData);
      
      console.log('✅ Indicadores PAC Programado frescos cargados:', freshData);
    } catch (error) {
      console.error('❌ Error al cargar indicadores PAC Programado:', error);
      setIndicadoresPacProgramadoError('Error al cargar indicadores PAC Programado. Por favor, intente nuevamente.');
      
      if (indicadoresPacProgramado.procesos_pac_programado === 0) {
        const cached = loadFromCache(cacheKey);
        if (cached) {
          setIndicadoresPacProgramado(cached.data);
          setIndicadoresPacProgramadoLastUpdate(cached.timestamp);
          setIndicadoresPacProgramadoError('Mostrando indicadores PAC Programado en caché. Algunos datos pueden no estar actualizados.');
          setIndicadoresPacProgramadoInitialized(true);
        }
      }
    } finally {
      setIndicadoresPacProgramadoLoading(false);
    }
  }, [isCacheValid, loadFromCache, loadIndicadoresPacProgramadoFromSupabase, saveToCache, indicadoresPacProgramado.procesos_pac_programado]);

  // ✅ FUNCIÓN para cargar datos PAC Vigente desde Supabase
  const loadPacVigenteFromSupabase = useCallback(async () => {
    console.log('🌐 Cargando PAC Vigente desde Supabase...');
    
    try {
      const { data: pacData, error } = await supabase
        .from('pac_vigente')
        .select('*');

      if (error) throw error;
      
      console.log(`✅ PAC Vigente obtenido de Supabase: ${pacData?.length || 0} registros`);
      return pacData || [];
    } catch (error) {
      console.error('❌ Error al cargar PAC Vigente de Supabase:', error);
      throw error;
    }
  }, []);

  const loadPacVigente = useCallback(async (forceRefresh = false) => {
    console.log(`🔄 loadPacVigente llamado. forceRefresh: ${forceRefresh}`);
    
    const cacheKey = 'pac_vigente_cache';
    
    if (!forceRefresh && pacVigenteData.length > 0 && isCacheValid(cacheKey)) {
      console.log('⚡ Ya tenemos PAC Vigente válido en contexto');
      return;
    }
    
    if (!forceRefresh && !pacVigenteData.length) {
      if (isCacheValid(cacheKey)) {
        console.log('⚡ Caché PAC Vigente válido - Carga INSTANTÁNEA');
        const cached = loadFromCache(cacheKey);
        if (cached) {
          setPacVigenteData(cached.data);
          setPacVigenteLastUpdate(cached.timestamp);
          setPacVigenteError(null);
          setPacVigenteInitialized(true);
          return;
        }
      } else {
        console.log('🕒 Caché PAC Vigente expirado, cargando como placeholder...');
        const cached = loadFromCache(cacheKey);
        if (cached) {
          setPacVigenteData(cached.data);
          setPacVigenteLastUpdate(cached.timestamp);
        }
      }
    }

    console.log('🌐 Cargando PAC Vigente desde servidor...');
    setPacVigenteLoading(true);
    setPacVigenteError(null);
    
    try {
      const freshData = await loadPacVigenteFromSupabase();
      
      setPacVigenteData(freshData);
      const updateTime = new Date();
      setPacVigenteLastUpdate(updateTime);
      setPacVigenteInitialized(true);
      
      saveToCache(cacheKey, freshData);
      
      console.log(`✅ PAC Vigente fresco cargado: ${freshData.length} registros`);
    } catch (error) {
      console.error('❌ Error al cargar PAC Vigente:', error);
      setPacVigenteError('Error al cargar PAC Vigente. Por favor, intente nuevamente.');
      
      if (pacVigenteData.length === 0) {
        const cached = loadFromCache(cacheKey);
        if (cached) {
          setPacVigenteData(cached.data);
          setPacVigenteLastUpdate(cached.timestamp);
          setPacVigenteError('Mostrando datos en caché de PAC Vigente. Algunos datos pueden no estar actualizados.');
          setPacVigenteInitialized(true);
        }
      }
    } finally {
      setPacVigenteLoading(false);
    }
  }, [isCacheValid, loadFromCache, loadPacVigenteFromSupabase, saveToCache, pacVigenteData.length]);

  // ✅ FUNCIÓN para refresh manual (PAC Vigente + indicadores PAC Programado)
  const handleRefreshPac = useCallback(() => {
    console.log('🔄 Refresh manual PAC solicitado');
    loadPacVigente(true);
    loadIndicadoresPacVigente(true);
    loadIndicadoresPacProgramado(true); // ✅ Agregar refresh de indicadores programado
  }, [loadPacVigente, loadIndicadoresPacVigente, loadIndicadoresPacProgramado]);

  // ✅ INICIALIZACIÓN automática (datos PAC Vigente)
  useEffect(() => {
    if (!pacVigenteInitialized) {
      console.log('🚀 Inicializando PAC Vigente...');
      loadPacVigente(false);
    }
  }, [pacVigenteInitialized, loadPacVigente]);

  // ✅ INICIALIZACIÓN automática (indicadores PAC Vigente)
  useEffect(() => {
    if (!indicadoresPacVigenteInitialized) {
      console.log('🚀 Inicializando indicadores PAC Vigente...');
      loadIndicadoresPacVigente(false);
    }
  }, [indicadoresPacVigenteInitialized, loadIndicadoresPacVigente]);

  // ✅ INICIALIZACIÓN automática (indicadores PAC Programado)
  useEffect(() => {
    if (!indicadoresPacProgramadoInitialized) {
      console.log('🚀 Inicializando indicadores PAC Programado...');
      loadIndicadoresPacProgramado(false);
    }
  }, [indicadoresPacProgramadoInitialized, loadIndicadoresPacProgramado]);

  // ✅ ACTUALIZACIÓN automática cada 5 minutos (PAC Vigente + indicadores PAC Programado)
  useEffect(() => {
    if (!pacVigenteInitialized && !indicadoresPacVigenteInitialized && !indicadoresPacProgramadoInitialized) return;

    const interval = setInterval(() => {
      const pacVigenteCacheKey = 'pac_vigente_cache';
      const indicadoresPacVigenteCacheKey = 'indicadores_pac_vigente_cache';
      const indicadoresPacProgramadoCacheKey = 'indicadores_pac_programado_cache';
      
      if (!isCacheValid(pacVigenteCacheKey)) {
        console.log('🔄 Caché PAC Vigente expirado - Actualizando automáticamente...');
        loadPacVigente(false);
      }

      if (!isCacheValid(indicadoresPacVigenteCacheKey)) {
        console.log('🔄 Caché indicadores PAC Vigente expirado - Actualizando automáticamente...');
        loadIndicadoresPacVigente(false);
      }

      if (!isCacheValid(indicadoresPacProgramadoCacheKey)) {
        console.log('🔄 Caché indicadores PAC Programado expirado - Actualizando automáticamente...');
        loadIndicadoresPacProgramado(false);
      }
    }, 60000); // Verificar cada minuto

    return () => clearInterval(interval);
  }, [
    pacVigenteInitialized, 
    indicadoresPacVigenteInitialized, 
    indicadoresPacProgramadoInitialized,
    isCacheValid, 
    loadPacVigente, 
    loadIndicadoresPacVigente,
    loadIndicadoresPacProgramado
  ]);

  // ✅ ESTADÍSTICAS CALCULADAS PAC Vigente (mantenidas para compatibilidad/fallback)
  const pacVigenteStats = React.useMemo(() => {
    if (!pacVigenteData.length) return {
      totalProcesos: 0,
      totalEntidades: 0,
      procesosPorMes: {},
      procesosConvocados: 0
    };

    // Agrupar por proceso único
    const procesosUnicos = {};
    pacVigenteData.forEach(item => {
      const key = `${item.ruc_entidad}_${item.mes_previsto}_${item.descripcion_proceso}`;
      if (!procesosUnicos[key]) {
        procesosUnicos[key] = item;
      }
    });

    const procesos = Object.values(procesosUnicos);
    const entidadesUnicas = new Set(procesos.map(p => p.ruc_entidad)).size;
    
    // Procesos convocados (que tienen fecha de convocatoria)
    const procesosConvocados = procesos.filter(p => p.fechaConvocatoria).length;

    // Procesos por mes
    const procesosPorMes = {};
    procesos.forEach(proceso => {
      const mes = proceso.mes_previsto || 'Sin mes';
      procesosPorMes[mes] = (procesosPorMes[mes] || 0) + 1;
    });

    return {
      totalProcesos: procesos.length,
      totalEntidades: entidadesUnicas,
      procesosPorMes,
      procesosConvocados
    };
  }, [pacVigenteData]);

  const value = {
    // PAC Vigente
    pacVigenteData,
    pacVigenteLoading,
    pacVigenteError,
    pacVigenteLastUpdate,
    pacVigenteInitialized,
    pacVigenteStats,
    
    // ✅ Indicadores PAC Vigente precalculados
    indicadoresPacVigente,
    indicadoresPacVigenteLoading,
    indicadoresPacVigenteError,
    indicadoresPacVigenteLastUpdate,
    indicadoresPacVigenteInitialized,
    
    // ✅ Indicadores PAC Programado precalculados
    indicadoresPacProgramado,
    indicadoresPacProgramadoLoading,
    indicadoresPacProgramadoError,
    indicadoresPacProgramadoLastUpdate,
    indicadoresPacProgramadoInitialized,
    
    // Funciones
    loadPacVigente,
    loadIndicadoresPacVigente,
    loadIndicadoresPacProgramado, // ✅ Nueva función
    handleRefreshPac,
    isCacheValidPac: (type) => {
      if (type === 'vigente') {
        return isCacheValid('pac_vigente_cache');
      } else if (type === 'indicadores_vigente') {
        return isCacheValid('indicadores_pac_vigente_cache');
      } else if (type === 'indicadores_programado') {
        return isCacheValid('indicadores_pac_programado_cache');
      }
      return false;
    }
  };

  return (
    <PacContext.Provider value={value}>
      {children}
    </PacContext.Provider>
  );
};